import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, GuestRoute } from './guards';

import AuthLayout from '../components/layout/AuthLayout';
import RGBBackground from '../components/layout/RGBBackground';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
// import CartWishlistDrawer from '../components/common/CartWishlistDrawer';
import PurchaseTimerWidget from '../components/common/PurchaseTimerWidget';
import CyberMouseFollower from '../components/common/CyberMouseFollower';

import Home from '../pages/Home';
import AllGames from '../pages/AllGames';
import Platforms from '../pages/Platforms';
import GameDetails from '../pages/GameDetails';
import Cart from '../pages/Cart';
import Wishlist from '../pages/Wishlist';
import Checkout from '../pages/Checkout';
import Blog from '../pages/Blog';
import BlogDetail from '../pages/BlogDetail';
import Contact from '../pages/Contact';
import Profile from '../pages/Profile';
import FAQs from '../pages/FAQs';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsOfService from '../pages/TermsOfService';
import Library from '../pages/Library';
import OrderHistory from '../pages/OrderHistory';
import NotFound from '../pages/NotFound';

const Auth = lazy(() => import('../pages/Auth'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const VerifyOtp = lazy(() => import('../pages/VerifyOtp'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Maintenance = lazy(() => import('../pages/Maintenance'));

/* ── Custom Cyber Loading Spinner for Suspense Fallback ── */
const LoadingSpinner = () => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
    <div className="w-12 h-12 rounded-full border-4 border-cyber-blue/20 border-t-cyber-blue animate-spin shadow-[0_0_15px_rgba(0,240,255,0.4)]"></div>
    <span className="text-xs font-cyber tracking-widest text-cyber-blue uppercase animate-pulse">Initializing Data Stream...</span>
  </div>
);

/* ── Main layout wrapper (header + footer + overlays) ── */
function MainLayout({ children }) {
  return (
    <div className="relative min-h-screen text-slate-100 flex flex-col justify-between selection:bg-cyber-blue/30 selection:text-white">
      <RGBBackground />
      <CyberMouseFollower />
      <Header />
      <PurchaseTimerWidget />
      <main className="flex-1 w-full relative">
        <Suspense fallback={<LoadingSpinner />}>
          {children}
        </Suspense>
      </main>
      <Footer />
      {/* <CartWishlistDrawer /> */}
    </div>
  );
}

function UserRoutes() {
  return (
    <Routes>
      {/* ── Public store pages (with header/footer) ── */}
      <Route path="/" element={<MainLayout><Home /></MainLayout>} />
      <Route path="/all-games" element={<MainLayout><AllGames /></MainLayout>} />
      <Route path="/platforms" element={<MainLayout><Platforms /></MainLayout>} />
      <Route path="/game/:slug" element={<MainLayout><GameDetails /></MainLayout>} />
      <Route path="/cart" element={<MainLayout><Cart /></MainLayout>} />
      <Route path="/wishlist" element={<MainLayout><Wishlist /></MainLayout>} />
      <Route path="/blog" element={<MainLayout><Blog /></MainLayout>} />
      <Route path="/blog/:id" element={<MainLayout><BlogDetail /></MainLayout>} />
      <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />
      <Route path="/faq" element={<MainLayout><FAQs /></MainLayout>} />
      <Route path="/privacy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
      <Route path="/terms" element={<MainLayout><TermsOfService /></MainLayout>} />
      {/* ── Protected pages (with header/footer) ── */}
      <Route path="/profile" element={<MainLayout><ProtectedRoute><Profile /></ProtectedRoute></MainLayout>} />
      <Route path="/checkout" element={<MainLayout><ProtectedRoute><Checkout /></ProtectedRoute></MainLayout>} />
      <Route path="/library" element={<MainLayout><ProtectedRoute><Library /></ProtectedRoute></MainLayout>} />
      <Route path="/order-history" element={<MainLayout><ProtectedRoute><OrderHistory /></ProtectedRoute></MainLayout>} />
      {/* ── Auth pages (no header/footer) ── */}
      <Route path="/login" element={<GuestRoute> <AuthLayout><Suspense fallback={<LoadingSpinner />}><Auth /></Suspense></AuthLayout> </GuestRoute>} />
      <Route path="/register" element={<Navigate to="/login" state={{ activeTab: 'register' }} replace />} />
      <Route path="/forgot-password" element={<GuestRoute> <AuthLayout><Suspense fallback={<LoadingSpinner />}><ForgotPassword /></Suspense></AuthLayout> </GuestRoute>} />
      <Route path="/verify-otp" element={<AuthLayout><Suspense fallback={<LoadingSpinner />}><VerifyOtp /></Suspense></AuthLayout>} />
      <Route path="/reset-password" element={<AuthLayout><Suspense fallback={<LoadingSpinner />}><ResetPassword /></Suspense></AuthLayout>} />
      {/* ── Maintenance page ── */}
      <Route path="/maintenance" element={<Suspense fallback={<LoadingSpinner />}><Maintenance /></Suspense>} />
      {/* ── 404 fallback ── */}
      <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
    </Routes>
  );    
}

export default UserRoutes;
